/**
 * Every word and number on a repair-set card, derived from the set itself.
 *
 * Pure and separate from the components because this is where the card can lie.
 * A set is USUALLY five questions and can be three when the bank is thin in that
 * skill, so nothing here may hardcode five, and the minutes have to follow from
 * the count rather than from a constant. The same shape feeds the score report
 * and the Home card so the two cannot drift into saying different things about
 * the same set.
 *
 * Returns null when there is no card to show. A test where no skill was missed
 * twice gets nothing, which is the correct answer and not an empty box.
 */

import { skillFamilyShort } from "./labels.js";

export type RepairCardSet = {
  skill_family: string;
  test_correct: number;
  test_total: number;
  done: number;
  total: number;
  complete: boolean;
};

export type RepairCardCopy = {
  label: string;
  /** What the test showed. The reason the card exists. */
  headline: string;
  /** How much is left, and how long it takes. */
  detail: string;
  cta: string;
  /** For the Home card's progress line; empty before they have started. */
  progress: string;
};

/** Roughly how long one of these questions takes, in minutes. */
const MINUTES_PER_QUESTION = 1.2;

export function repairCardCopy(
  set: RepairCardSet | null,
  /**
   * Which test the headline refers to. On Home the newest open set IS the last
   * test; on a score report the student may be looking at an older one, and
   * calling it "your last practice test" would be wrong the moment they have
   * taken two.
   */
  context: "home" | "report" = "home",
): RepairCardCopy | null {
  if (!set || set.complete) return null;

  // FAIL CLOSED ON DATA THAT CANNOT BE TRUE. This function exists because it is
  // where the card can lie, so an impossible set (more done than there are
  // questions, a negative count, a fractional one) must produce NO card rather
  // than confident nonsense like "6 of 5 done" or "0 questions left, keep going".
  const sane =
    Number.isInteger(set.total) && set.total > 0 &&
    Number.isInteger(set.done) && set.done >= 0 && set.done < set.total &&
    Number.isInteger(set.test_total) && set.test_total > 0 &&
    Number.isInteger(set.test_correct) && set.test_correct >= 0 &&
    set.test_correct <= set.test_total;
  if (!sane) return null;

  const skill = skillFamilyShort(set.skill_family);
  const started = set.done > 0;
  const left = Math.max(0, set.total - set.done);
  // Never "about 0 minutes", and never a fractional one.
  const minutes = Math.max(2, Math.round(left * MINUTES_PER_QUESTION));

  const qs = `${left} ${left === 1 ? "question" : "questions"}`;
  const mins = `about ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  const thatTest = context === "report" ? "this test" : "your last test";

  // THE SCORE USED TO LEAD AND IT NO LONGER DOES.
  //
  // The headline was "Reading was 3 of 8 on your last practice test." Measured
  // funnel: 26 students got a set, 9 started it, and 9 of 9 who started finished.
  // The artifact converts perfectly and the offer converts at a third, so the card
  // is the thing to change, not the set.
  //
  // An outside review of this exact card, 2026-09-29, put the miss count first
  // among the reasons not to tap it: the score report directly above has already
  // told them how they did, and repeating it sells the next step by reopening the
  // bad news. The skill name stays, because the student still needs to know what
  // the questions are about; the numbers go.
  //
  // "None from that test" is new here and is the one genuinely good thing about
  // these sets that the card never said. The server deliberately draws questions
  // the student has not seen, and a student who assumes this is a re-run of
  // answers they already got wrong has a good reason not to bother.
  //
  // Placement was the other recommendation and was already true: the card sits
  // directly under the score on the report, above the per-section breakdown and
  // well above "Back home".
  //
  // NOT DONE, deliberately: the same review called the minutes figure the most
  // damaging element and then kept it in its own rewrite. It is small, honest and
  // derived from the count, and a student deciding whether to start is owed the
  // size. Kept, in second position rather than first.
  return {
    label: started ? "Pick up where you left off" : "Quick follow-up",
    headline: started
      ? `${qs} left in your ${skill} set.`
      : `${qs} on ${skill}, none from ${thatTest}.`,
    // Capitalised when it stands alone, lower case when it continues a sentence.
    // The started branch used to render the bare fragment "about 2 minutes."
    detail: started
      ? `${mins.charAt(0).toUpperCase()}${mins.slice(1)}.`
      : `They focus on the skill you missed most, ${mins}.`,
    cta: started ? "Keep going" : "Start",
    progress: started ? `${set.done} of ${set.total} done` : "",
  };
}
